import { useState, useEffect, ChangeEvent, FocusEvent, Dispatch, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { FieldArray } from 'react-final-form-arrays';
import debounce from 'lodash.debounce';
import { useUnmount } from 'react-use';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { getUserRoleIsOmbudsman } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { IKnmLinkTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmLinkTypeSelectOption';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KNM_LINKS, ROUTE_KNM, ROUTE_PM } from 'CorePackage/MainModule/routes/routes';
import { FORM_LINK_TYPE_FIELD_ID } from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { ConfirmModal } from 'UIPackage/MainModule/components/ConfirmModal';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { knmListFindKnm, knmListSetFindData } from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';

import { IKnmErpLinksForm } from '../interfaces/IKnmErpLinksForm';
import styles from './KnmErpLinks.module.scss';

const ERP_ID_MIN_LENGTH = 12;

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ?? ''}${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${knm.inn ? `(ИНН: ${knm.inn}` : ''}${
    knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''
  })${knm.controllingOrganization ?? ''}${knm.prosecutorOrganization ?? ''} ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

const generateLinkInfoText = (knmLink: IKnmLink) =>
  `${knmLink.knmType ?? ''} ${knmLink.subjectName ?? ''} ${knmLink.inn ? `(ИНН: ${knmLink.inn})` : ''}${
    knmLink.ogrn ? ` (ОГРН: ${knmLink.ogrn})` : ''
  } ${knmLink.knoOrganizationName ?? ''} ${knmLink.prosecutorOfficeName ?? ''} ${formatDate({
    date: knmLink.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knmLink.stopDate ? `${formatDate({ date: knmLink.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'} ${
    knmLink.type?.name ? `(Основание связывания: ${knmLink.type?.name}` : ''
  })`;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm: IKnmIndex;
}

interface IKnmErpLinksProps {
  formLinkType: IKnmErpLinksForm['linkType'] | undefined;
  erpId: IKnm['erpId'] | undefined;
  watchOnly?: boolean;
}

interface IKnmErpLinksStoreProps {
  isOmbudsman: boolean | undefined;
  formRef: IKnmFormRef | undefined;
  knms: IKnmIndex[];
}

const KnmErpLinksWithoutStore = ({
  formLinkType,
  erpId,
  watchOnly,
  formRef,
  isOmbudsman,
  knms,
}: IKnmErpLinksProps & IKnmErpLinksStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const [isAddingFieldsActive, setIsAddingFieldsActive] = useState(false);
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [lastKnmsAutocompleteOptions, setLastKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[]>([]);
  const [deletingLinkErpId, setDeletingLinkErpId] = useState<string | undefined>();
  const [isFounded, setIsFounded] = useState(false);

  useEffect(() => {
    knmsAutocompleteOptions && setLastKnmsAutocompleteOptions(knmsAutocompleteOptions);
  }, [knmsAutocompleteOptions]);

  useEffect(() => {
    if (!isAddingFieldsActive) {
      setErpIdInputValue('');
      setIsErpIdSelected(false);
      formRef?.current.change('linkType');
    }
  }, [isAddingFieldsActive, formRef]);

  useEffect(() => {
    isFounded &&
      setKnmsAutocompleteOptions(
        knms.map(knm => {
          const knmText = `${generateKnmInfoText(knm)}`;

          return {
            value: knmText,
            label: knmText,
            knm,
          };
        }),
      );
  }, [isFounded, knms]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  const debouncedGetKnms = debounce(findKnms, 3000);

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      setErpIdInputValue(value);
      debouncedGetKnms('');
      setKnmsAutocompleteOptions(undefined);
      setIsErpIdSelected(true);
    },
    [debouncedGetKnms],
  );

  function findKnms(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);
      dispatch(
        knmListFindKnm(
          { erpId: paramErpId, isKnmLinkQuery: true, ignoreProsecutorOffice: true, ignoreKno: true },
          2000,
        ),
      );
    }
  }

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value: valueEdited } = e.target;

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedGetKnms(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedGetKnms('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedGetKnms],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
    formRef?.current.change('linkType');
    formRef?.current.resetFieldState('linkType');
  }, [formRef]);

  const openLinkFields = useCallback(() => setIsAddingFieldsActive(true), []);
  const closeLinkFields = useCallback(() => setIsAddingFieldsActive(false), []);

  const addLink = useCallback(
    (knm: IKnmIndex) => {
      const formTypeValue: IKnmLinkTypeSelectOption | undefined = formRef?.current.getFieldState('linkType')?.value;

      if (formTypeValue) {
        const type = deleteObjectProperties(formTypeValue, ['label', 'value']);

        if (push) {
          push('links', {
            erpId: knm.erpId,
            type,
            id: knm.id,
            knmType: knm.knmType,
            subjectName: knm.organizationName,
            inn: knm.inn,
            ogrn: knm.ogrn,
            knoOrganizationName: knm.controllingOrganization,
            prosecutorOfficeName: knm.prosecutorOrganization,
            startDate: formatDateLocale({ date: knm.startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            stopDate: knm.stopDate
              ? formatDateLocale({ date: knm.stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
              : null,
          });
        }
        setErpIdInputValue('');
        setIsErpIdSelected(false);
        formRef?.current.change('linkType');
        formRef?.current.resetFieldState('linkType');
      }
    },
    [formRef, push],
  );

  const deleteLink = useCallback(
    (linkedErpId: string) => {
      const formLinks: IKnmLink[] | undefined = formRef?.current.getFieldState('links')?.value;
      const deletingLinkIndex = formLinks?.findIndex(formLink => formLink.erpId === linkedErpId);

      if (typeof deletingLinkIndex === 'number' && deletingLinkIndex >= 0 && remove) {
        remove('links', deletingLinkIndex);
      }
    },
    [formRef, remove],
  );

  const handleSaveButtonClick = useCallback(() => {
    const knmAutocompleteOption = lastKnmsAutocompleteOptions?.find(
      lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === erpIdInputValue,
    );
    const knm = knmAutocompleteOption?.knm;

    if (knm) {
      addLink(knm);
    }
  }, [addLink, erpIdInputValue, lastKnmsAutocompleteOptions]);

  const makeLinkDeleteButtonClickHandler = (linkedErpId: string) => () =>
    !watchOnly && setDeletingLinkErpId(linkedErpId);

  const resetDeletingLinkErpId = useCallback(() => setDeletingLinkErpId(undefined), []);

  const handleDeleteLinkModalApply = useCallback(() => {
    if (!watchOnly && deletingLinkErpId) {
      deleteLink(deletingLinkErpId);
    }
    resetDeletingLinkErpId();
  }, [deleteLink, deletingLinkErpId, resetDeletingLinkErpId, watchOnly]);

  return (
    <Section
      title="Связанные КНМ"
      id={KNM_LINKS}
      type="list"
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={watchOnly || isAddingFieldsActive}
          onClick={openLinkFields}
        />
      }
    >
      {isAddingFieldsActive && (
        <Row>
          <AutoComplete
            type="field"
            options={knmsAutocompleteOptions}
            noFoundMessage="КНМ, соответствующие указанному учетному номеру, не найдены"
            handleAutoCompleteSelect={handleAutoCompleteSelect}
          >
            <TextInput
              id="linkErpId"
              name="linkErpId"
              placeholder="Введите учетный номер КНМ"
              value={erpIdInputValue}
              clearable={isErpIdSelected}
              onChange={handleTextInputChange}
              onBlur={handleTextInputBlur}
              onClear={handleTextInputClear}
            />
          </AutoComplete>
          <SelectInputFieldAutoOptions
            className={styles.LinkField}
            id={FORM_LINK_TYPE_FIELD_ID}
            name="linkType"
            placeholder="Выберите основание связывания КНМ"
            optionName={CommonCatalogsNamesEnum.KNM_LINK_TYPES}
            disabled={watchOnly}
            size="medium"
          />
          {!isOmbudsman && (
            <Button
              className={styles.SaveButton}
              text="Сохранить"
              color="default"
              size="medium"
              type="button"
              id="saveButton"
              disabled={watchOnly || !isErpIdSelected || !formLinkType?.value}
              onClick={handleSaveButtonClick}
            />
          )}
          <Button
            className={styles.CancelButton}
            variant="link"
            color="primary"
            text="Отмена"
            size="medium"
            type="button"
            onClick={closeLinkFields}
          />
        </Row>
      )}

      <FieldArray name="links">
        {({ fields }) => (
          <ul className={styles.Links}>
            {fields.map((name, index) => {
              const isPm = fields.value?.[index]?.knmType === null;
              const linkId = fields.value?.[index]?.id;

              return (
                <li className={styles.Link} key={name}>
                  <Link className={styles.ErpIdLink} to={isPm ? ROUTE_PM(linkId) : ROUTE_KNM(linkId)} target="blank">
                    {fields.value?.[index]?.erpId}
                  </Link>
                  <div className={styles.LinkText}>{generateLinkInfoText(fields.value?.[index])}</div>
                  <CloseButton
                    className={styles.LinkDeleteButton}
                    disabled={watchOnly}
                    onClick={makeLinkDeleteButtonClickHandler(fields.value?.[index]?.erpId)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </FieldArray>

      <ConfirmModal
        visible={Boolean(deletingLinkErpId)}
        title="Подтверждение"
        text={`Удалить связь проверки ${erpId} с проверкой ${deletingLinkErpId}?`}
        applyButtonText="Удалить"
        cancelButtonText="Отменить"
        onApply={handleDeleteLinkModalApply}
        onClose={resetDeletingLinkErpId}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpLinksStoreProps => ({
  formRef: getKnmFormRef(state),
  isOmbudsman: getUserRoleIsOmbudsman(state),
  knms: getKnmListFindData(state),
});

export const KnmErpLinks = connect(mapStateToProps)(KnmErpLinksWithoutStore);
